import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Route, useHistory, useParams, useRouteMatch } from 'react-router';

import { AppContext } from './AppContext';
import { delay } from '../util/ExtraMethods';
import { uxTracking } from '../util/ux-tracking';
import useFingerprint from '../util/useFingerprint';
import Greetings from '../components/Review/Greetings/Greetings';
import ReviewForm from '../components/Review/ReviewForm/ReviewForm';
import ReviewTree from '../components/Review/ReviewTree/ReviewTree';
import ReviewError from '../components/Review/ReviewError/ReviewError';
import ReviewResult from '../components/Review/ReviewResult/ReviewResult';
import OpeningPage from '../components/Review/ReviewPage/Opening.review.page';
import RedirectFlytree from '../components/Review/RedirectFlytree/RedirectFlytree';
import {
  useApi,
  reviewPath,
  reviewPrefix,
  usePartnerTheme,
} from '../util/lib';

import css from './Review.module.css';

const Review = (props) => {
  usePartnerTheme();
  const {legacy} = props;
  const {code} = useParams();
  const history = useHistory();
  const [review, setReview] = useState();
  const [reviewLinks, setReviewLinks] = useState();
  const {setShop} = useContext(AppContext);

  const isOnErrorPage = useRouteMatch(reviewPath(code, 'error', legacy));

  const api = useApi('flytree');
  const visitor = useFingerprint();
  // const visitor = {
  //   visitorId: +new Date(),
  //   components:{
  //     timezone:{value:'Indonesia/Jakarta'},
  //     platform:{value:'chrome'}
  //   }
  // }
  const goTo = useCallback(async (page) => {
    await delay(400);
    const path = reviewPath(code, page, legacy);
    if (page === 'error' && isOnErrorPage) return;
    history.push(path);
  }, [code, history, legacy, isOnErrorPage]);

  const prepare = useCallback(async() => {
    try {
      if (!visitor) return;

      let response = await api({
        method: 'get',
        responseType: 'json',
        url: `/reviews/${code}`,
        params: {
          open: true,
          visitorId: visitor.visitorId,
          timezone: visitor.components.timezone.value,
          platform: visitor.components.platform.value,
        }
      });
      if (!response.data || !response.data.review) {
        throw new Error('Failed to fetch review data');
      }
      const review = response.data.review;
      uxTracking.identify(review.code, {
        phone: review.phone,
        rating: review.rating,
        feedback: review.feedback,
        firstName: review.firstName,
      });
      setReview(review);

      const {partner} = review;
      setShop(partner);

      if (review.feedback) {
        goTo('result');
        return;
      }
      const clinikoId = review.thirdpartyData &&
        review.thirdpartyData.clinikoId;
      const clinikoBusinessId = review.thirdpartyData &&
        review.thirdpartyData.clinikoBusinessId;

      response = await api({
        method: 'get',
        responseType: 'json',
        url: '/reviews/links',
        params: {
          clinikoId,
          clinikoBusinessId,
          partnerId: review.partnerId,
        }
      });
      if (!response.data || !response.data.links) {
        throw new Error('Failed to fetch review links');
      }
      setReviewLinks(response.data.links);

      if (partner && partner.ignoreExperience) {
        return goTo('tree');
      }

      goTo('opening');
    } catch (error) {
      uxTracking.anonymize();
      console.error(error);
      goTo('error');
    }
  }, [visitor, code, goTo, api, setShop]);

  useEffect(() => {
    if (!review) prepare();
  }, [prepare, review]);

  const onReviewed = useCallback(async (data) => {
    setReview({ ...review, ...data });
    goTo('result');
  }, [review, goTo]);

  const prefix = reviewPrefix(legacy);
  const context = { api, legacy, review, reviewLinks };
  return (
    <div className={css.Review}>
      <div className={css.ReviewContent}>
        <Route path={`${prefix}/:code/opening`} exact>
          <OpeningPage {...context} />
        </Route>
        <Route path={`${prefix}/:code/error`} exact>
          <ReviewError {...context} />
        </Route>
        <Route path={`${prefix}/:code/result`} exact>
          <ReviewResult {...context} />
        </Route>
        <Route path={`${prefix}/:code/tree`} exact>
          <ReviewTree {...context} />
        </Route>
        <Route path={`${prefix}/:code/review-form-bad`} exact>
          <ReviewForm
            {...context}
            onReviewed={onReviewed}
            defaultTitle='We are sorry to hear you had a negative experience, please let us know what was wrong' />
        </Route>
        <Route path={`${prefix}/:code/review-form`} exact>
          <ReviewForm
            {...context}
            onReviewed={onReviewed}
            defaultTitle="Your review helps us grow" />
        </Route>
        <Route path={`${prefix}/:code/redirect/:platform`} exact>
          <RedirectFlytree {...context} />
        </Route>
        <Route path={`${prefix}/:code`} exact>
          <Greetings {...context}/>
        </Route>
        <div className={css.PoweredBy}>
          Powered by{' '}
          <span
            onClick={() =>
              (window.location.href = 'https://www.flytree.co')
            }
          >
          Flytree
          </span>
        </div>
      </div>
    </div>
  );
};
export default Review;
